<template>
  <div class="doc-layout" :class="{ 'is-mobile': isMobile }">
    <!-- 顶部导航栏 -->
    <header class="top-header">
      <div class="header-left">
        <div class="logo-section" @click="handleLogoClick">
          <i class="el-icon-reading"></i>
          <span class="logo-text" v-if="!isMobile">万维智慧云文档</span>
          <span class="logo-text" v-else>万维智慧云</span>
        </div>
        
        <!-- 文集面包屑 -->
        <div class="breadcrumb-section">
          <el-breadcrumb separator="/">
            <el-breadcrumb-item>
              <router-link :to="isAdminRoute ? '/admin/collections' : '/'">文集列表</router-link>
            </el-breadcrumb-item>
            <el-breadcrumb-item v-if="currentCollection">{{ currentCollection.collectionName }}</el-breadcrumb-item>
          </el-breadcrumb>
        </div>
        
      </div>
      
      <div class="header-right">
        <div class="header-actions">
          <!-- 移动端侧边栏切换按钮 -->
          <div 
            class="mobile-sidebar-toggle" 
            @click="toggleMobileSidebar"
          >
            <i class="el-icon-s-fold"></i>
          </div>
          <!-- 导出按钮（仅管理员可见） -->
          <el-tooltip v-if="isAdminUser && currentCollection && !isMobile" content="导出文集为Word" placement="bottom">
            <el-button 
              size="medium" 
              icon="el-icon-download" 
              :type="isMobile ? 'text' : 'success'"
              :circle="isMobile"
              @click="handleExportCollection"
              class="export-btn"
            >
              <span class="btn-text">导出Word</span>
            </el-button>
          </el-tooltip>
          <!-- 复制文集按钮 -->
          <el-tooltip v-if="canEdit && currentCollection && !isMobile" content="复制整个文集" placement="bottom">
            <el-button 
              size="medium" 
              icon="el-icon-document-copy" 
              :type="isMobile ? 'text' : 'warning'"
              :circle="isMobile"
              @click="handleCopyCurrentCollection"
              class="export-btn"
            >
              <span class="btn-text">复制文集</span>
            </el-button>
          </el-tooltip>
          <!-- 搜索框（仅非admin路由显示） -->
          <div class="search-container" v-if="!isAdminRoute">
            <el-input
              v-model="searchKeyword"
              placeholder="搜索文档..."
              prefix-icon="el-icon-search"
              class="search-box"
              clearable
              @keyup.enter.native="handleSearchEnter"
              @input="handleSearchInput"
            >
            </el-input>
          </div>
        </div>
        <el-dropdown 
          @command="handleUserCommand" 
          class="user-menu"
          :visible-arrow="false"
          trigger="click"
        >
          <div class="user-info-box">
            <el-avatar :size="32" icon="el-icon-user-solid" :style="{background: roleColor}"></el-avatar>
            <span class="username-text">{{ userInfo.nickname || userInfo.username }}</span>
            <i class="el-icon-arrow-down el-icon--right"></i>
          </div>
          <el-dropdown-menu slot="dropdown" :append-to-body="true">
            <el-dropdown-item disabled>
              <div class="user-card">
                <div class="user-card-name">{{ userInfo.username }}</div>
                <el-tag :type="roleTagType" size="mini">{{ roleText }}</el-tag>
              </div>
            </el-dropdown-item>
            <el-dropdown-item v-if="isAdminUser" command="admin" divided>
              <i class="el-icon-s-tools"></i> 进入后台
            </el-dropdown-item>
            <el-dropdown-item :divided="!isAdminUser" command="logout">
              <i class="el-icon-switch-button"></i> 退出登录
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
      </div>
    </header>
    
    <!-- 主体内容 -->
    <div class="main-wrapper" :class="{ 'sidebar-open': mobileSidebarOpen }" @click="handleWrapperClick">
      <!-- 左侧目录树 -->
      <aside class="sidebar-tree" :class="{ 'collapsed': sidebarCollapsed, 'mobile-open': mobileSidebarOpen }" @click.stop>
        <div class="tree-header">
          <div class="tree-title">
            <i class="el-icon-folder-opened"></i>
            <span v-show="!sidebarCollapsed && !isMobile" class="tree-title-text">{{ currentCollection ? currentCollection.collectionName : '文档目录' }}</span>
          </div>
          <div class="tree-actions">
            <el-tooltip content="折叠全部" placement="bottom" v-show="!sidebarCollapsed">
              <el-button type="text" icon="el-icon-minus" size="small" @click="collapseAll"></el-button>
            </el-tooltip>
            <el-tooltip content="展开全部" placement="bottom" v-show="!sidebarCollapsed">
              <el-button type="text" icon="el-icon-plus" size="small" @click="expandAll"></el-button>
            </el-tooltip>
            <!-- <el-tooltip content="刷新" placement="bottom" v-show="!sidebarCollapsed">
              <el-button type="text" icon="el-icon-refresh" size="small" @click="loadCategoryTree"></el-button>
            </el-tooltip> -->
            <el-tooltip :content="sidebarCollapsed ? '展开目录' : '收起目录'" placement="bottom" v-show="!isMobile">
              <el-button type="text" :icon="sidebarCollapsed ? 'el-icon-s-unfold' : 'el-icon-s-fold'" size="small" @click="toggleSidebar"></el-button>
            </el-tooltip>
          </div>
        </div>
        
        <div class="tree-content" v-show="!sidebarCollapsed" v-loading="treeLoading" @contextmenu.prevent="handleTreeContentRightClick">
          <el-tree
            ref="categoryTree"
            :data="categoryTree"
            :props="treeProps"
            node-key="id"
            :expand-on-click-node="true"
            :highlight-current="true"
            :default-expand-all="true"
            @node-click="handleNodeClick"
            @node-contextmenu="handleNodeRightClick"
            class="doc-tree"
          >
            <span class="tree-node" slot-scope="{ node, data }">
              <span class="node-content">
                <i :class="getNodeIcon(data)" class="node-icon"></i>
                <span class="node-label" :title="node.label">{{ node.label }}</span>
              </span>
              <span class="node-actions" v-if="canEdit">
                <el-dropdown @command="(cmd) => handleTreeAction(cmd, data)" trigger="click" @click.native.stop>
                  <span class="action-trigger">
                    <i class="el-icon-more"></i>
                  </span>
                  <el-dropdown-menu slot="dropdown">
                    <template v-if="data.type === 'category'">
                      <el-dropdown-item command="addDoc">
                        <i class="el-icon-document-add"></i> 添加文档
                      </el-dropdown-item>
                      <el-dropdown-item command="addSubCategory" v-if="data.level < 3">
                        <i class="el-icon-folder-add"></i> 添加子目录
                      </el-dropdown-item>
                      <el-dropdown-item command="rename" divided>
                        <i class="el-icon-edit"></i> 重命名
                      </el-dropdown-item>
                      <el-dropdown-item command="delete">
                        <i class="el-icon-delete"></i> 删除
                      </el-dropdown-item>
                    </template>
                    <template v-if="data.type === 'document'">
                      <el-dropdown-item command="copyDoc">
                        <i class="el-icon-document-copy"></i> 复制文档
                      </el-dropdown-item>
                      <el-dropdown-item command="rename">
                        <i class="el-icon-edit"></i> 重命名
                      </el-dropdown-item>
                      <el-dropdown-item command="setOrder">
                        <i class="el-icon-sort"></i> 设置排序
                      </el-dropdown-item>
                      <el-dropdown-item command="delete">
                        <i class="el-icon-delete"></i> 删除
                      </el-dropdown-item>
                    </template>
                  </el-dropdown-menu>
                </el-dropdown>
              </span>
            </span>
          </el-tree>
          
          <el-empty v-if="categoryTree.length === 0" description="暂无文档" :image-size="80"></el-empty>
        </div>
      </aside>
      
      <!-- 右侧内容区 -->
      <main class="content-area">
        <!-- 显示搜索结果或正常内容 -->
        <SearchResults 
          v-if="isSearching"
          :searchKeyword="currentSearchKeyword"
          :searchResults="searchResults"
          :loading="searchLoading"
          @result-click="handleSearchResultClick"
        />
        <router-view v-else @refresh="loadCategoryTree" />
      </main>
    </div>
    
    <!-- 右键菜单 -->
    <div 
      v-show="contextMenuVisible" 
      class="context-menu"
      :style="{left: contextMenuLeft + 'px', top: contextMenuTop + 'px'}"
      @click="contextMenuVisible = false"
    >
      <!-- 空白处右键菜单 -->
      <template v-if="!contextMenuData.type">
        <div class="context-menu-item" @click="handleContextMenu('addRootCategory')">
          <i class="el-icon-folder-add"></i> 添加一级目录
        </div>
      </template>
      
      <!-- 节点右键菜单 -->
      <template v-else>
        <div class="context-menu-item" v-if="contextMenuData.type === 'category'" @click="handleContextMenu('addDoc')">
          <i class="el-icon-document-add"></i> 添加文档
        </div>
        <div class="context-menu-item" v-if="contextMenuData.type === 'category' && contextMenuData.level < 3" @click="handleContextMenu('addSubCategory')">
          <i class="el-icon-folder-add"></i> 添加子目录
        </div>
        <div class="context-menu-divider" v-if="contextMenuData.type === 'category'"></div>
        <div class="context-menu-item" v-if="contextMenuData.type === 'document'" @click="handleContextMenu('copyDoc')">
          <i class="el-icon-document-copy"></i> 复制文档
        </div>
        <div class="context-menu-item" @click="handleContextMenu('rename')">
          <i class="el-icon-edit"></i> 重命名
        </div>
        <div class="context-menu-item" v-if="contextMenuData.type === 'document'" @click="handleContextMenu('setOrder')">
          <i class="el-icon-sort"></i> 设置排序
        </div>
        <div class="context-menu-item danger" @click="handleContextMenu('delete')">
          <i class="el-icon-delete"></i> 删除
        </div>
      </template>
    </div>
    
    <!-- 创建/编辑目录对话框 -->
    <el-dialog
      :title="categoryDialogTitle"
      :visible.sync="categoryDialogVisible"
      width="500px"
      :close-on-click-modal="false"
    >
      <el-form :model="categoryForm" :rules="categoryRules" ref="categoryForm" label-width="90px">
        <el-form-item label="目录名称" prop="categoryName">
          <el-input 
            v-model="categoryForm.categoryName" 
            placeholder="请输入目录名称"
            maxlength="50"
            show-word-limit
          ></el-input>
        </el-form-item>
        
        <el-form-item label="层级信息" v-if="!categoryForm.categoryId">
          <el-tag type="info" size="small">
            {{ categoryForm.level === 1 ? '一级目录' : categoryForm.level === 2 ? '二级目录' : '三级目录' }}
            <span v-if="categoryForm.parentName"> - 父目录: {{ categoryForm.parentName }}</span>
          </el-tag>
        </el-form-item>
        
        <el-form-item label="排序" prop="order">
          <el-input-number v-model="categoryForm.order" :min="0" :max="999" controls-position="right"></el-input-number>
          <span class="form-tip">数字越小越靠前</span>
        </el-form-item>
      </el-form>
      
      <div slot="footer">
        <el-button @click="categoryDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSaveCategory" :loading="categoryCreating">
          {{ categoryCreating ? '保存中...' : '确定' }}
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { userUtils, authUtils } from '@/utils/auth'
import { CATEGORY_PATHS, DOCUMENT_PATHS, COLLECTION_PATHS } from '@/api/paths'
import SearchResults from '@/pages/SearchResults.vue'

export default {
  name: 'DocumentLayout',
  components: {
    SearchResults
  },
  data() {
    return {
      userInfo: {},
      categoryTree: [],
      allCategories: [],
      treeLoading: false,
      searchKeyword: '',
      searchResults: [],
      searchLoading: false,
      isSearching: false,
      currentSearchKeyword: '',
      currentCollectionId: null,
      currentCollection: null,
      sidebarCollapsed: false,
      treeProps: {
        children: 'children',
        label: 'label'
      },
      categoryDialogVisible: false,
      categoryDialogTitle: '新建目录',
      categoryCreating: false,
      categoryForm: {
        categoryId: null,
        categoryName: '',
        level: 1,
        parentId: null,
        parentName: '',
        order: 0
      },
      categoryRules: {
        categoryName: [
          { required: true, message: '请输入目录名称', trigger: 'blur' },
          { min: 1, max: 50, message: '长度在 1 到 50 个字符', trigger: 'blur' }
        ]
      },
      contextMenuVisible: false,
      contextMenuLeft: 0,
      contextMenuTop: 0,
      contextMenuData: {},
      mobileSidebarOpen: false,
      screenWidth: window.innerWidth
    }
  },
  computed: {
    isMobile() {
      // 优先使用设备检测，避免横屏时被误判为桌面端
      if (typeof navigator !== 'undefined') {
        const ua = navigator.userAgent || navigator.vendor || ''
        
        // 1. 检测明确的移动设备标识
        const mobileRegex = /(android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini)/i
        if (mobileRegex.test(ua)) {
          return true
        }
        
        // 2. 检测移动设备的User Agent特征
        const mobilePlatforms = /Mobile|mobile|Mobi|mobi/i
        if (mobilePlatforms.test(ua)) {
          return true
        }
        
        // 3. 检测触摸设备并结合屏幕尺寸
        if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
          // 使用屏幕的最小尺寸来判断，避免横屏影响
          const screenMinSize = window.screen ? Math.min(window.screen.width, window.screen.height) : 0
          const screenMaxSize = window.screen ? Math.max(window.screen.width, window.screen.height) : 0
          
          // 如果最小尺寸小于等于768，或者最大尺寸小于等于1024，很可能是移动设备 
          if (screenMinSize <= 768 || (screenMaxSize <= 1024 && screenMinSize <= 768)) {
            return true
          }
        }
      }
      
      // 4. 兜底：如果以上都不满足，但当前窗口宽度小于等于768，也认为是移动端
      // 这主要是为了处理一些特殊情况和桌面端的小窗口
      return this.screenWidth <= 768
    },
    canEdit() {
      return authUtils.isEditor()
    },
    isAdminUser() {
      return authUtils.isAdmin()
    },
    roleText() {
      const roleMap = {
        'admin': '管理员',
        'editor': '编辑员',
        'viewer': '普通用户'
      }
      return roleMap[this.userInfo.role] || '未知'
    },
    roleTagType() {
      const typeMap = {
        'admin': 'danger',
        'editor': 'warning',
        'viewer': 'info'
      }
      return typeMap[this.userInfo.role] || 'info'
    },
    roleColor() {
      const colorMap = {
        'admin': '#f56c6c',
        'editor': '#e6a23c',
        'viewer': '#909399'
      }
      return colorMap[this.userInfo.role] || '#409EFF'
    },
    isAdminRoute() {
      return this.$route.path.startsWith('/admin')
    }
  },
  mounted() {
    this.getUserInfo()
    this.initCollection()
    this.loadCategoryTree()
    
    // 移动端默认打开侧边栏
    if (this.isMobile) {
      this.mobileSidebarOpen = true
    }
    
    // 检查是否需要高亮并滚动到指定节点
    this.$nextTick(() => {
      this.checkAndHighlightNode()
    })
    
    // 屏幕宽度监听用于区分移动端与桌面端
    this.__onResize = () => { this.screenWidth = window.innerWidth }
    window.addEventListener('resize', this.__onResize)
  },
  
  beforeDestroy() {
    if (this.__onResize) {
      window.removeEventListener('resize', this.__onResize)
    }
  },
  
  watch: {
    '$route.params.id': {
      handler(newId, oldId) {
        // 只有在文集ID变化时才重新初始化（排除首次加载）
        if (oldId !== undefined && newId !== oldId) {
          this.initCollection()
          this.loadCategoryTree()
        }
      }
    },
    '$route.query.highlight': {
      handler(newVal) {
        if (newVal) {
          this.$nextTick(() => {
            this.checkAndHighlightNode()
          })
        }
      },
      immediate: true
    },
    screenWidth(newWidth) {
      this.screenWidth = newWidth
    }
  },
  methods: {
    getUserInfo() {
      this.userInfo = userUtils.getUserInfo() || {}
    },
    
    handleLogoClick() {
      if (this.isAdminRoute) {
        this.$router.push('/admin/collections')
      } else {
        this.$router.push('/')
      }
    },
    
    initCollection() {
      // DocumentLayout 现在专门用于文集内容显示
      // 检查是否在文集详情页面（包括admin路由）
      if (this.$route.path.startsWith('/collections/') || this.$route.path.startsWith('/admin/collections/')) {
        this.currentCollectionId = this.$route.params.id
        this.loadCollectionInfo()
      } else {
        // 如果不在文集详情页面，重定向到文集列表
        if (this.$route.path.startsWith('/admin')) {
          this.$router.push('/admin/collections')
        } else {
          this.$router.push('/')
        }
      }
    },
    
    async loadCollectionInfo() {
      if (!this.currentCollectionId) return
      
      try {
        const response = await this.$get(COLLECTION_PATHS.GET_COLLECTION_DETAIL + '?collectionId=' + this.currentCollectionId)
        if (response.data && response.data.code === 200) {
          this.currentCollection = response.data.data
        }
      } catch (error) {
        console.error('加载文集信息失败', error)
      }
    },
    
    async loadCategoryTree() {
      this.treeLoading = true
      try {
        let treeUrl = CATEGORY_PATHS.GET_TREE
        let listUrl = CATEGORY_PATHS.GET_LIST
        
        // 如果在文集详情页面，添加文集ID参数
        if (this.currentCollectionId) {
          treeUrl += `?collectionId=${this.currentCollectionId}`
          listUrl += `?collectionId=${this.currentCollectionId}`
        }
        
        const [treeRes, listRes] = await Promise.all([
          this.$get(treeUrl),
          this.$get(listUrl)
        ])
        
        if (treeRes.data && treeRes.data.code === 200) {
          this.categoryTree = this.buildTreeNodes(treeRes.data.data || [])
          
          // 如果当前没有选中文档，自动跳转到第一个文档
          if (!this.$route.params.docId && this.categoryTree.length > 0) {
            const firstDoc = this.findFirstDocument(this.categoryTree)
            if (firstDoc) {
              this.$nextTick(() => {
                if (this.isAdminRoute) {
                  this.$router.replace(`/admin/collections/${this.currentCollectionId}/doc/${firstDoc.documentId}`)
                } else {
                  this.$router.replace(`/collections/${this.currentCollectionId}/doc/${firstDoc.documentId}`)
                }
                // 跳转后高亮第一个文档节点
                this.$nextTick(() => {
                  this.highlightAndScrollToNode(`doc-${firstDoc.documentId}`)
                })
              })
            }
          }
        }
        
        if (listRes.data && listRes.data.code === 200) {
          this.allCategories = listRes.data.data || []
        }
        
        // 目录树加载完成后，检查是否需要高亮节点
        this.$nextTick(() => {
          this.checkAndHighlightNode()
        })
      } catch (error) {
        this.$message.error('加载目录失败')
        console.error(error)
      } finally {
        this.treeLoading = false
      }
    },
    
    buildTreeNodes(data) {
      const nodes = []
      
      data.forEach(item => {
        const node = {
          id: `cat-${item.categoryId}`,
          label: item.categoryName,
          type: 'category',
          categoryId: item.categoryId,
          level: item.level,
          children: []
        }
        
        if (item.children && item.children.length > 0) {
          node.children.push(...this.buildTreeNodes(item.children))
        }
        
        if (item.documents && item.documents.length > 0) {
          // 对文档按order字段排序
          const sortedDocs = [...item.documents].sort((a, b) => (a.order || 0) - (b.order || 0))
          sortedDocs.forEach(doc => {
            node.children.push({
              id: `doc-${doc.documentId}`,
              label: doc.title,
              type: 'document',
              documentId: doc.documentId,
              categoryId: item.categoryId,
              order: doc.order || 0
            })
          })
        }
        
        nodes.push(node)
      })
      
      return nodes
    },
    
    findFirstDocument(nodes) {
      // 递归查找第一个文档
      for (const node of nodes) {
        if (node.type === 'document') {
          return node
        }
        if (node.children && node.children.length > 0) {
          const doc = this.findFirstDocument(node.children)
          if (doc) return doc
        }
      }
      return null
    },
    
    getNodeIcon(data) {
      if (data.type === 'document') {
        return 'el-icon-document'
      }
      return 'el-icon-folder'
    },
    
    handleNodeClick(data) {
      // 只处理文档节点的点击，目录节点的展开/折叠由expand-on-click-node自动处理
      if (data.type === 'document') {
        // 如果正在搜索状态，退出搜索
        if (this.isSearching) {
          this.isSearching = false
          this.searchKeyword = ''
          this.searchResults = []
          this.currentSearchKeyword = ''
        }
        
        // 移动端点击文档时关闭侧边栏
        if (this.isMobile) {
          this.mobileSidebarOpen = false
        }
        
        // 根据当前路由判断跳转路径
        if (this.isAdminRoute) {
          this.$router.push(`/admin/collections/${this.currentCollectionId}/doc/${data.documentId}`)
        } else {
          this.$router.push(`/collections/${this.currentCollectionId}/doc/${data.documentId}`)
        }
      }
      // 目录节点点击时，什么都不做，让树组件自动处理展开/折叠
    },
    
    handleNodeRightClick(event, data) {
      if (!this.canEdit) return
      
      // 阻止默认右键菜单
      event.preventDefault()
      event.stopPropagation()
      
      // 设置当前选中节点
      this.$refs.categoryTree.setCurrentKey(data.id)
      
      // 显示右键菜单
      this.contextMenuData = data
      this.contextMenuLeft = event.clientX
      this.contextMenuTop = event.clientY
      this.contextMenuVisible = true
      
      // 点击其他地方关闭菜单
      const closeMenu = () => {
        this.contextMenuVisible = false
        document.removeEventListener('click', closeMenu)
      }
      setTimeout(() => {
        document.addEventListener('click', closeMenu)
      }, 100)
    },
    
    handleTreeContentRightClick(event) {
      if (!this.canEdit) return
      
      // 检查是否点击的是tree节点，如果是则不处理（让节点的右键事件处理）
      const target = event.target
      if (target.closest('.el-tree-node__content')) {
        return
      }
      
      // 阻止默认右键菜单
      event.preventDefault()
      event.stopPropagation()
      
      // 显示空白处右键菜单
      this.contextMenuData = {} // 空对象表示空白处
      this.contextMenuLeft = event.clientX
      this.contextMenuTop = event.clientY
      this.contextMenuVisible = true
      
      // 点击其他地方关闭菜单
      const closeMenu = () => {
        this.contextMenuVisible = false
        document.removeEventListener('click', closeMenu)
      }
      setTimeout(() => {
        document.addEventListener('click', closeMenu)
      }, 100)
    },
    
    handleContextMenu(command) {
      this.contextMenuVisible = false
      this.handleTreeAction(command, this.contextMenuData)
    },
    
    // 搜索输入处理
    handleSearchInput() {
      // 如果搜索词为空且正在搜索，退出搜索状态
      if (!this.searchKeyword.trim() && this.isSearching) {
        this.isSearching = false
        this.searchResults = []
        this.currentSearchKeyword = ''
      }
    },
    
    // 按回车或点击搜索按钮
    handleSearchEnter() {
      if (!this.searchKeyword.trim()) {
        this.$message.warning('请输入搜索关键词')
        return
      }
      this.performSearch()
    },
    
    // 执行搜索
    async performSearch() {
      if (!this.searchKeyword.trim()) {
        return
      }
      
      this.isSearching = true
      this.currentSearchKeyword = this.searchKeyword.trim()
      this.searchLoading = true
      this.searchResults = []
      
      try {
        const response = await this.$get(DOCUMENT_PATHS.SEARCH_DOCUMENTS, {
          params: { keyword: this.currentSearchKeyword }
        })
        
        if (response.data && response.data.code === 200) {
          this.searchResults = response.data.data || []
        } else {
          this.searchResults = []
          this.$message.error(response.data.msg || '搜索失败')
        }
      } catch (error) {
        console.error('搜索失败:', error)
        this.$message.error('搜索失败')
        this.searchResults = []
      } finally {
        this.searchLoading = false
      }
    },
    
    // 点击搜索结果（现在搜索结果会直接在新标签页打开，这个方法可能不再被调用）
    handleSearchResultClick(doc) {
      // 如果需要在当前页面打开（保留此方法以防需要）
      this.isSearching = false
      this.searchKeyword = ''
      this.searchResults = []
      this.currentSearchKeyword = ''
      
      // 跳转到文档
      this.$router.push(`/collections/${doc.collectionId}/doc/${doc.documentId}`)
    },
    
    // 检查URL参数并高亮对应节点
    checkAndHighlightNode() {
      const highlightParam = this.$route.query.highlight
      if (highlightParam && this.categoryTree.length > 0) {
        // 立即尝试定位
        this.highlightAndScrollToNode(highlightParam)
      } else if (!highlightParam && this.$route.params.docId && this.categoryTree.length > 0) {
        // 如果没有 highlight 参数但有 docId，高亮当前文档节点
        this.highlightAndScrollToNode(`doc-${this.$route.params.docId}`)
      }
    },
    
    // 高亮并滚动到指定节点
    highlightAndScrollToNode(nodeKey) {
      if (!this.$refs.categoryTree) {
        return false
      }
      
      try {
        // 设置当前节点高亮
        this.$refs.categoryTree.setCurrentKey(nodeKey)
        
        // 获取节点
        const node = this.$refs.categoryTree.getNode(nodeKey)
        
        if (node) {
          // 确保父节点展开
          let parent = node.parent
          while (parent && parent.level > 0) {
            parent.expanded = true
            parent = parent.parent
          }
          
          // 等待DOM更新后滚动
          this.$nextTick(() => {
            // 减少延迟时间
            setTimeout(() => {
              // 获取tree-content容器（带滚动条的容器）
              const treeContent = document.querySelector('.tree-content')
              
              if (treeContent) {
                // 查找当前高亮的节点元素
                const currentNode = treeContent.querySelector('.el-tree-node.is-current')
                
                if (currentNode) {
                  // 计算节点相对于容器的位置
                  const nodeRect = currentNode.getBoundingClientRect()
                  const containerRect = treeContent.getBoundingClientRect()
                  const scrollTop = treeContent.scrollTop
                  
                  // 计算目标滚动位置（节点居中）
                  const targetScroll = scrollTop + nodeRect.top - containerRect.top - (containerRect.height / 2) + (nodeRect.height / 2)
                  
                  // 直接滚动，不使用动画
                  treeContent.scrollTop = targetScroll
                }
              }
            }, 100)
          })
          return true
        }
      } catch (error) {
        console.error('高亮节点失败:', error)
      }
      return false
    },
    
    handleTreeAction(command, data) {
      switch(command) {
        case 'addRootCategory':
          // 添加一级目录
          this.showCategoryDialog(null, 1, '')
          break
        case 'addDoc':
          // 根据当前路由判断跳转路径
          if (this.isAdminRoute) {
            this.$router.push(`/admin/collections/${this.currentCollectionId}/doc/create?categoryId=${data.categoryId}`)
          } else {
            this.$router.push(`/collections/${this.currentCollectionId}/doc/create?categoryId=${data.categoryId}`)
          }
          break
        case 'addSubCategory':
          this.showCategoryDialog(data.categoryId, data.level + 1, data.label)
          break
        case 'rename':
          if (data.type === 'category') {
            this.showEditCategoryDialog(data)
          } else if (data.type === 'document') {
            this.showRenameDocumentDialog(data)
          }
          break
        case 'setOrder':
          if (data.type === 'document') {
            this.showSetDocumentOrderDialog(data)
          }
          break
        case 'copyDoc':
          if (data.type === 'document') {
            this.handleCopyDocument(data)
          }
          break
        case 'delete':
          if (data.type === 'category') {
            this.handleDeleteCategory(data)
          } else if (data.type === 'document') {
            this.handleDeleteDocument(data)
          }
          break
      }
    },
    
    showRenameDocumentDialog(data) {
      this.$prompt('请输入新的文档名称', '重命名文档', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        inputValue: data.label,
        inputPattern: /.+/,
        inputErrorMessage: '文档名称不能为空'
      }).then(async ({ value }) => {
        try {
          const response = await this.$post(DOCUMENT_PATHS.UPDATE_DOCUMENT, {
            documentId: data.documentId,
            title: value
          })
          if (response.data && response.data.code === 200) {
            this.$message.success('重命名成功')
            this.loadCategoryTree()
          }
        } catch (error) {
          this.$message.error('重命名失败')
        }
      }).catch(() => {})
    },
    
    showSetDocumentOrderDialog(data) {
      this.$prompt('请输入排序值（数字越小越靠前）', '设置文档排序', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        inputValue: data.order || 0,
        inputPattern: /^\d+$/,
        inputErrorMessage: '请输入有效的数字'
      }).then(async ({ value }) => {
        try {
          const response = await this.$post(DOCUMENT_PATHS.UPDATE_DOCUMENT, {
            documentId: data.documentId,
            order: parseInt(value)
          })
          if (response.data && response.data.code === 200) {
            this.$message.success('设置成功')
            this.loadCategoryTree()
          }
        } catch (error) {
          this.$message.error('设置失败')
        }
      }).catch(() => {})
    },
    
    async handleDeleteDocument(data) {
      try {
        await this.$confirm('确定要删除这个文档吗？删除后不可恢复。', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        const response = await this.$post(DOCUMENT_PATHS.DELETE_DOCUMENT, {
          documentId: data.documentId
        })
        
        if (response.data && response.data.code === 200) {
          this.$message.success('删除成功')
          this.loadCategoryTree()
          // 如果当前正在查看这个文档，跳转到文集首页
          if (this.$route.params.docId == data.documentId) {
            if (this.isAdminRoute) {
              this.$router.push(`/admin/collections/${this.currentCollectionId}`)
            } else {
              this.$router.push(`/collections/${this.currentCollectionId}`)
            }
          }
        }
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('删除失败')
        }
      }
    },
    
    async handleCopyDocument(data) {
      try {
        await this.$confirm(`确定要复制文档 "${data.label}" 吗？`, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'info'
        })
        
        this.treeLoading = true
        const response = await this.$post(DOCUMENT_PATHS.COPY_DOCUMENT, {
          documentId: data.documentId
        })
        
        if (response.data && response.data.code === 200) {
          this.$message.success('复制成功')
          const newDoc = response.data.data
          await this.loadCategoryTree()
          if (newDoc && newDoc.documentId) {
            if (this.isAdminRoute) {
              this.$router.push(`/admin/collections/${this.currentCollectionId}/doc/${newDoc.documentId}`)
            } else {
              this.$router.push(`/collections/${this.currentCollectionId}/doc/${newDoc.documentId}`)
            }
            this.$nextTick(() => {
              this.highlightAndScrollToNode(`doc-${newDoc.documentId}`)
            })
          }
        } else {
          this.$message.error(response.data.msg || '复制失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('复制失败')
          console.error(error)
        }
      } finally {
        this.treeLoading = false
      }
    },
    
    showCategoryDialog(parentId = null, level = 1, parentName = '') {
      this.categoryDialogTitle = '新建目录'
      this.categoryForm = {
        categoryId: null,
        categoryName: '',
        level: level,
        parentId: parentId,
        parentName: parentName,
        order: 0
      }
      this.categoryDialogVisible = true
      this.$nextTick(() => {
        this.$refs.categoryForm && this.$refs.categoryForm.clearValidate()
      })
    },
    
    showEditCategoryDialog(data) {
      this.categoryDialogTitle = '编辑目录'
      const category = this.allCategories.find(c => c.categoryId === data.categoryId)
      if (category) {
        // 获取父目录名称
        let parentName = ''
        if (category.parentId) {
          const parent = this.allCategories.find(c => c.categoryId === category.parentId)
          parentName = parent ? parent.categoryName : ''
        }
        
        this.categoryForm = {
          categoryId: category.categoryId,
          categoryName: category.categoryName,
          level: category.level,
          parentId: category.parentId,
          parentName: parentName,
          order: category.order || 0
        }
        this.categoryDialogVisible = true
      }
    },
    
    async handleSaveCategory() {
      try {
        await this.$refs.categoryForm.validate()
      } catch {
        return
      }
      
      this.categoryCreating = true
      try {
        const params = {
          categoryName: this.categoryForm.categoryName,
          level: this.categoryForm.level,
          order: this.categoryForm.order,
          collectionId: this.currentCollectionId
        }
        
        if (this.categoryForm.level > 1) {
          params.parentId = this.categoryForm.parentId
        }
        
        let response
        if (this.categoryForm.categoryId) {
          params.categoryId = this.categoryForm.categoryId
          response = await this.$post(CATEGORY_PATHS.UPDATE, params)
        } else {
          response = await this.$post(CATEGORY_PATHS.CREATE, params)
        }
        
        if (response.data && response.data.code === 200) {
          this.$message.success(this.categoryForm.categoryId ? '更新成功' : '创建成功')
          this.categoryDialogVisible = false
          this.loadCategoryTree()
        }
      } catch (error) {
        this.$message.error(this.categoryForm.categoryId ? '更新失败' : '创建失败')
        console.error(error)
      } finally {
        this.categoryCreating = false
      }
    },
    
    async handleDeleteCategory(data) {
      try {
        await this.$confirm('确定要删除这个目录吗？如果目录下有文档或子目录将无法删除。', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        const response = await this.$post(CATEGORY_PATHS.DELETE, {
          categoryId: data.categoryId
        })
        
        if (response.data && response.data.code === 200) {
          this.$message.success('删除成功')
          this.loadCategoryTree()
        }
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error(error.response?.data?.msg || '删除失败')
        }
      }
    },
    
    toggleSidebar() {
      this.sidebarCollapsed = !this.sidebarCollapsed
    },
    
    toggleMobileSidebar() {
      this.mobileSidebarOpen = !this.mobileSidebarOpen
      if (this.mobileSidebarOpen) {
        this.sidebarCollapsed = false
      }
    },
    
    handleWrapperClick() {
      // 移动端点击内容区域时关闭侧边栏
      if (this.mobileSidebarOpen && this.isMobile) {
        this.mobileSidebarOpen = false
      }
    },
    
    // 折叠全部
    collapseAll() {
      const tree = this.$refs.categoryTree
      if (tree) {
        // 获取所有节点的key
        const allKeys = []
        const getAllKeys = (nodes) => {
          nodes.forEach(node => {
            if (node.type === 'category') {
              allKeys.push(node.id)
            }
            if (node.children && node.children.length > 0) {
              getAllKeys(node.children)
            }
          })
        }
        getAllKeys(this.categoryTree)
        
        // 折叠所有节点
        allKeys.forEach(key => {
          const node = tree.getNode(key)
          if (node) {
            node.expanded = false
          }
        })
      }
    },
    
    // 展开全部
    expandAll() {
      const tree = this.$refs.categoryTree
      if (tree) {
        // 获取所有节点的key
        const allKeys = []
        const getAllKeys = (nodes) => {
          nodes.forEach(node => {
            if (node.type === 'category') {
              allKeys.push(node.id)
            }
            if (node.children && node.children.length > 0) {
              getAllKeys(node.children)
            }
          })
        }
        getAllKeys(this.categoryTree)
        
        // 展开所有节点
        allKeys.forEach(key => {
          const node = tree.getNode(key)
          if (node) {
            node.expanded = true
          }
        })
      }
    },
    
    handleUserCommand(command) {
      if (command === 'logout') {
        this.logout()
      } else if (command === 'admin') {
        this.$router.push('/admin/users')
      }
    },
    
    logout() {
      this.$confirm('确定要退出登录吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        authUtils.clearAuth()
        this.$message.success('已退出登录')
        this.$router.push('/login')
      }).catch(() => {})
    },
    
    // 复制整个文集
    async handleCopyCurrentCollection() {
      if (!this.currentCollectionId) {
        this.$message.warning('请先选择文集')
        return
      }
      const colName = this.currentCollection ? this.currentCollection.collectionName : ''
      try {
        await this.$confirm(`确定要复制整个文集 "${colName}" 及其下所有文档吗？`, '复制文集', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'info'
        })
      } catch (error) {
        if (error === 'cancel') return
      }
      
      const loading = this.$loading({
        lock: true,
        text: '正在复制整个文集，请稍候...',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      })
      
      try {
        const response = await this.$post(COLLECTION_PATHS.COPY_COLLECTION, {
          collectionId: this.currentCollectionId
        })
        
        if (response.data && response.data.code === 200) {
          this.$message.success('文集复制成功')
          const newCol = response.data.data
          if (newCol && newCol.collectionId) {
            if (this.isAdminRoute) {
              this.$router.push(`/admin/collections/${newCol.collectionId}`)
            } else {
              this.$router.push(`/collections/${newCol.collectionId}`)
            }
          }
        } else {
          this.$message.error(response.data.msg || '复制文集失败')
        }
      } catch (error) {
        this.$message.error('复制文集失败')
        console.error(error)
      } finally {
        loading.close()
      }
    },
    
    // 导出文集为Word
    async handleExportCollection() {
      if (!this.currentCollection) {
        this.$message.warning('请先选择文集')
        return
      }
      
      // 确认是否导出文集
      try {
        await this.$confirm(`确定要导出文集"${this.currentCollection.collectionName}"为Word文档吗？`, '导出文集', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'info'
        })
      } catch (error) {
        // 用户取消导出
        if (error === 'cancel') {
          return
        }
      }
      
      try {
        const loading = this.$loading({
          lock: true,
          text: '正在导出文集，请稍候...',
          spinner: 'el-icon-loading',
          background: 'rgba(0, 0, 0, 0.7)'
        })
        
        // 导入API基础URL和认证工具
        const API_BASE_URL = require('@/utils/request').API_BASE_URL
        const { tokenUtils } = require('@/utils/auth')
        
        // 构建请求URL
        const token = tokenUtils.getToken()
        if (!token) {
          loading.close()
          this.$message.error('未登录或登录已过期')
          return
        }
        
        const fileName = encodeURIComponent(this.currentCollection.collectionName + '.docx')
        const url = `${API_BASE_URL}/export/collection/${this.currentCollectionId}/word?fileName=${fileName}`
        
        // 使用XMLHttpRequest检查响应类型
        const xhr = new XMLHttpRequest()
        xhr.open('GET', url, true)
        xhr.setRequestHeader('Authorization', `Bearer ${token}`)
        
        xhr.onload = function() {
          loading.close()
          
          if (xhr.status === 200) {
            const contentType = xhr.getResponseHeader('Content-Type')
            
            // 检查是否返回JSON（缓存URL）
            if (contentType && contentType.includes('application/json')) {
              try {
                // 将arraybuffer转换为字符串
                const text = new TextDecoder('utf-8').decode(xhr.response)
                const result = JSON.parse(text)
                
                if (result.cached && result.url) {
                  // 使用缓存的OSS URL直接下载
                  const a = document.createElement('a')
                  a.href = result.url
                  a.download = this.currentCollection.collectionName + '.docx'
                  document.body.appendChild(a)
                  a.click()
                  document.body.removeChild(a)
                  this.$message.success('导出成功')
                } else {
                  this.$message.error('导出失败')
                }
              } catch (e) {
                console.error('解析响应失败:', e)
                this.$message.error('解析响应失败')
              }
            } else {
              // 直接返回的文件流
              const blob = new Blob([xhr.response], { 
                type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
              })
              const blobUrl = window.URL.createObjectURL(blob)
              const a = document.createElement('a')
              a.href = blobUrl
              a.download = this.currentCollection.collectionName + '.docx'
              document.body.appendChild(a)
              a.click()
              window.URL.revokeObjectURL(blobUrl)
              document.body.removeChild(a)
              
              this.$message.success('导出成功')
            }
          } else {
            this.$message.error('导出失败')
          }
        }.bind(this)
        
        xhr.onerror = function() {
          loading.close()
          this.$message.error('导出失败')
        }.bind(this)
        
        // 设置响应类型为arraybuffer以支持两种响应
        xhr.responseType = 'arraybuffer'
        xhr.send()
        
      } catch (error) {
        console.error('导出失败:', error)
        this.$message.error('导出失败：' + error.message)
      }
    }
  }
}
</script>

<style scoped>
.doc-layout {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f5f7f9;
}

/* ===== 顶部导航栏 ===== */
.top-header {
  height: 60px;
  background: #fff;
  border-bottom: 1px solid #e4e7ed;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.04);
  z-index: 100;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 20px;
  flex: 1;
}

.logo-section {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 20px;
  font-weight: 600;
  color: #303133;
  cursor: pointer;
  transition: color 0.3s;
}

.logo-section i {
  font-size: 28px;
  color: #409EFF;
}

.logo-section:hover {
  color: #409EFF;
}

.breadcrumb-section {
  margin-left: 20px;
}

.breadcrumb-section ::v-deep .el-breadcrumb__item:last-child .el-breadcrumb__inner {
  color: #606266;
}

/* ===== 搜索容器 ===== */
.search-container {
  margin-right: 20px;
}

.search-box {
  width: 400px;
}

.search-box ::v-deep .el-input__inner {
  border-radius: 20px;
  background: #f5f7f9;
  border-color: transparent;
}

.search-box ::v-deep .el-input__inner:focus {
  background: #fff;
  border-color: #409EFF;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.mobile-sidebar-toggle {
  display: none;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  transition: color 0.3s;
}

.mobile-sidebar-toggle i {
  font-size: 28px;
  color: #409EFF;
}

.mobile-sidebar-toggle:hover {
  color: #409EFF;
}

.mobile-sidebar-toggle:hover i {
  color: #409EFF;
}

.user-menu {
  cursor: pointer;
}

.user-info-box {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 20px;
  transition: all 0.3s;
  cursor: pointer;
  border: 1px solid transparent;
}

.user-info-box:hover {
  background: #f5f7f9;
  border-color: #e4e7ed;
}

.user-info-box i {
  font-size: 12px;
  color: #909399;
  transition: transform 0.3s;
}

.user-menu.is-opened .user-info-box i {
  transform: rotate(180deg);
}

.username-text {
  font-size: 14px;
  color: #606266;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-card {
  padding: 5px 0;
  text-align: center;
}

.user-card-name {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 8px;
}

/* ===== 主体区域 ===== */
.main-wrapper {
  flex: 1;
  display: flex;
  overflow: hidden;
  height: calc(100vh - 60px);
}

/* ===== 左侧目录树 ===== */
.sidebar-tree {
  width: 300px;
  background: #fff;
  border-right: 1px solid #e4e7ed;
  display: flex;
  flex-direction: column;
  transition: width 0.3s ease;
  overflow: hidden;
}

.sidebar-tree.collapsed {
  width: 50px;
}

.tree-header {
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 15px;
  border-bottom: 1px solid #f0f2f5;
}

.sidebar-tree.collapsed .tree-header {
  justify-content: center;
  padding: 0 5px;
}

.tree-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

.sidebar-tree.collapsed .tree-title {
  display: none;
}

.tree-title i {
  font-size: 18px;
  color: #409EFF;
}

.tree-actions {
  display: flex;
  gap: 5px;
}

.tree-content {
  flex: 1;
  overflow-y: auto;
  padding: 10px 0 200px;
}

/* ===== 树形节点样式 ===== */
.doc-tree {
  background: transparent;
}

.doc-tree ::v-deep .el-tree-node__content {
  height: 36px;
  padding-right: 10px;
  border-radius: 4px;
  margin: 0 10px 2px 10px;
  transition: all 0.2s;
}

.doc-tree ::v-deep .el-tree-node__content:hover {
  background: #f5f7f9;
}

.doc-tree ::v-deep .el-tree-node.is-current > .el-tree-node__content {
  background: #ecf5ff;
  color: #409EFF;
  font-weight: 500;
}

.tree-node {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  overflow: hidden;
}

.node-content {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
  min-width: 0;
}

.node-icon {
  font-size: 16px;
  flex-shrink: 0;
}

.doc-tree ::v-deep .el-icon-folder {
  color: #E6A23C;
}

.doc-tree ::v-deep .el-icon-folder-opened {
  color: #F56C6C;
}

.doc-tree ::v-deep .el-icon-document {
  color: #409EFF;
}

.node-label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
}

.node-actions {
  opacity: 0;
  transition: opacity 0.2s;
  padding: 0 5px;
}

.tree-node:hover .node-actions {
  opacity: 1;
}

.action-trigger {
  padding: 2px 6px;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s;
}

.action-trigger:hover {
  background: #e4e7ed;
}

/* ===== 内容区域 ===== */
.content-area {
  flex: 1;
  overflow-y: auto;
  background: #fff;
}

/* ===== 对话框样式 ===== */
.form-tip {
  margin-left: 10px;
  font-size: 12px;
  color: #909399;
}

/* ===== 滚动条美化 ===== */
.tree-content::-webkit-scrollbar,
.content-area::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.tree-content::-webkit-scrollbar-thumb,
.content-area::-webkit-scrollbar-thumb {
  background: #d1dbe5;
  border-radius: 3px;
}

.tree-content::-webkit-scrollbar-thumb:hover,
.content-area::-webkit-scrollbar-thumb:hover {
  background: #b4bccc;
}

.tree-content::-webkit-scrollbar-track,
.content-area::-webkit-scrollbar-track {
  background: transparent;
}

/* ===== 右键菜单 ===== */
.context-menu {
  position: fixed;
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.1);
  padding: 4px 0;
  z-index: 9999;
  min-width: 150px;
}

.context-menu-item {
  padding: 10px 16px;
  font-size: 13px;
  color: #606266;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
}

.context-menu-item:hover {
  background: #f5f7f9;
  color: #409EFF;
}

.context-menu-item.danger {
  color: #F56C6C;
}

.context-menu-item.danger:hover {
  background: #fef0f0;
  color: #F56C6C;
}

.context-menu-divider {
  height: 1px;
  background: #e4e7ed;
  margin: 4px 0;
}

/* ===== 响应式 ===== */
.doc-layout.is-mobile .top-header {
  padding: 0 10px;
  height: 50px;
}

.doc-layout.is-mobile .header-left {
  flex: 1;
  min-width: 0;
}

.doc-layout.is-mobile .logo-section {
  font-size: 16px;
}

.doc-layout.is-mobile .logo-text {
  /* display: none; */
  margin-bottom: 3px;
}

.doc-layout.is-mobile .breadcrumb-section {
  display: none;
}

.doc-layout.is-mobile .header-actions {
  gap: 8px;
}

.doc-layout.is-mobile .search-container {
  margin: 0;
  order: -1;
}

.doc-layout.is-mobile .search-box {
  width: 140px;
}

.doc-layout.is-mobile .mobile-sidebar-toggle {
  display: flex !important;
  order: -2;
}

.doc-layout.is-mobile .export-btn {
  order: -1;
}

.doc-layout.is-mobile .tree-title-text {
  display: none !important;
}

.doc-layout.is-mobile .main-wrapper {
  position: relative;
}

.doc-layout.is-mobile .sidebar-tree {
  position: fixed;
  left: 0;
  top: 50px;
  height: calc(100vh - 50px);
  z-index: 1000;
  transform: translateX(-100%);
  transition: transform 0.3s ease;
  box-shadow: 2px 0 8px rgba(0,0,0,0.1);
}

.doc-layout.is-mobile .sidebar-tree.mobile-open:not(.collapsed) {
  transform: translateX(0);
}

.doc-layout.is-mobile .sidebar-tree.collapsed {
  width: 50px;
  transform: translateX(0);
}

.doc-layout.is-mobile .mobile-sidebar-toggle {
  display: flex !important;
  margin-right: 10px;
}

.doc-layout.is-mobile .export-btn .btn-text {
  display: none;
}

.doc-layout.is-mobile .export-btn {
  padding: 8px;
}

/* 点击遮罩层关闭侧边栏 */
.doc-layout.is-mobile .main-wrapper.sidebar-open::before {
  cursor: pointer;
}

.doc-layout.is-mobile .content-area {
  width: 100%;
}

.doc-layout.is-mobile .user-info-box {
  padding: 4px 8px;
}

.doc-layout.is-mobile .username-text {
  display: none;
}

/* 添加遮罩层 */
.doc-layout.is-mobile .main-wrapper::before {
  content: '';
  position: fixed;
  left: 0;
  top: 50px;
  width: 100%;
  height: calc(100vh - 50px);
  background: rgba(0,0,0,0.5);
  z-index: 999;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
}

.doc-layout.is-mobile .main-wrapper.sidebar-open::before {
  opacity: 1;
  visibility: visible;
}

@media (max-width: 480px) {
  .top-header {
    height: 45px;
    padding: 0 8px;
  }
  
  .header-actions {
    gap: 6px;
  }
  
  .search-box {
    width: 120px;
  }
  
  .export-btn {
    padding: 6px;
  }
  
  .export-btn .btn-text {
    display: none;
  }
  
  .user-info-box {
    padding: 4px 8px;
  }
  
  .sidebar-tree {
    width: 260px;
    top: 45px;
    height: calc(100vh - 45px);
  }
  
  .tree-header {
    height: 45px;
    padding: 0 10px;
  }
  
  .tree-content {
    padding: 8px 0 200px;
  }
  
  .content-area {
    padding: 0;
  }
}
</style>
